/**
 * The one editor, over the active cell.
 *
 * The other half of "read-only cells everywhere": a cell is a `<span>` of text
 * and there are thousands of them, so nothing about a cell may be expensive. An
 * editor may be, because there is exactly one of it. It is positioned over the
 * cell the way the selection rectangle is -- same spacer, same arithmetic -- so
 * it scrolls with the rows and needs no synchronisation.
 *
 * "Portaled" in the plan's sense means *not mounted per cell*, which this is.
 * An actual `createPortal` would put it outside the scroll container and it
 * would then have to be moved on every scroll event; here it simply belongs
 * where the rows are.
 */
import { useRef } from 'react'
import { useStore } from 'zustand'
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import { cellValidationError } from '../fields/behavior/validation'
import { editorFor } from '../fields/ui/edit'
import { cellValue } from '../fields/read'
import type { RowWindow } from '../rows/rowWindow'
import { columnLayout, type GridColumn } from './columns'
import { cellIndexes } from './selection'
import type { SelectionStore } from './selection-store'

export function CellEditorLayer({
  store,
  window,
  columns,
  rowHeight,
  gutter,
  context,
  onKeyDown,
  onCommit,
}: {
  store: SelectionStore
  window: RowWindow
  columns: readonly GridColumn[]
  rowHeight: number
  gutter: number
  context: FieldContext
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
  /** Whether the editor may close: `false` when the cell will not take it. */
  onCommit: () => boolean
}) {
  const { t } = useTranslation()
  const frame = useRef<HTMLDivElement>(null)
  const editing = useStore(store, (state) => state.editing)
  if (editing === null) return null

  const indexes = cellIndexes(editing.cell, window, columns)
  if (indexes === null) return null

  const column = columns[indexes.column]
  const row = window.rows[indexes.row - window.range.start]
  if (column === undefined || row === undefined) return null

  const editor = editorFor(column.field)
  if (editor === null) return null

  // What the cell will not accept, worked out from what is in the editor now --
  // so it appears as somebody types rather than after they try to leave.
  const problem = cellValidationError(column.field, editing.draft)

  const { offsets } = columnLayout(columns, gutter)
  const left = offsets[indexes.column]
  if (left === undefined) return null

  return (
    <div
      ref={frame}
      data-testid="cell-editor"
      // Focus leaving the editor commits it. On the frame rather than on the
      // control, because an editor may be more than one focusable thing -- a
      // rating is a symbol per star -- and moving between them is not leaving.
      // `focusout` bubbles, which is what lets one handler cover all of them.
      onBlur={(event) => {
        if (frame.current?.contains(event.relatedTarget) === true) return
        if (onCommit()) return
        // Refused. The editor is still open on what was typed, so the focus
        // belongs back in it -- otherwise the message is on screen with the
        // keyboard somewhere else, and the next key goes to the grid.
        //
        // Deferred, because this runs while the browser is part-way through
        // moving the focus somewhere: focusing now is undone a moment later by
        // the move that caused this event.
        const control = frame.current?.querySelector('input, textarea')
        setTimeout(() => {
          if (control instanceof HTMLElement) control.focus()
        }, 0)
      }}
      className="absolute z-20"
      style={{
        top: indexes.row * rowHeight,
        left,
        width: column.width,
        height: rowHeight,
      }}
    >
      <editor.Component
        field={column.field}
        context={context}
        invalid={problem !== null}
        // One editor over one cell, and it is opened to be typed into.
        autoFocus
        // Opened by Enter or a double-click when the draft is still what the
        // cell holds; opened by typing when it is not.
        selectOnFocus={
          editing.draft === draftFor(column.field, { id: editing.cell.rowId }, window)
        }
        value={editing.draft}
        label={t('grid.editing', { field: column.field.name })}
        onValueChange={(draft) => {
          store.getState().type(draft)
        }}
        onKeyDown={onKeyDown}
      />
      {problem === null ? null : (
        // Beside the cell rather than inside it: the editor is exactly one row
        // high, and a message that had to fit there would be a truncated
        // sentence saying nothing. `alert` because it appears in response to
        // what was just typed, which is the one case a screen reader should be
        // interrupted for.
        <div
          role="alert"
          className="absolute left-0 top-full z-30 w-max max-w-xs rounded-b border border-t-0 border-destructive bg-destructive px-2 py-1 text-xs text-destructive-foreground"
        >
          {t(`fieldErrors.${problem.key}`, problem.values ?? {})}
        </div>
      )}
    </div>
  )
}

/** What the editor should start from when a cell is opened. */
export function draftFor(field: Field, row: { id: number }, window: RowWindow): string {
  const editor = editorFor(field)
  if (editor === null) return ''
  const held = window.rows.find((candidate) => candidate.id === row.id)
  return held === undefined ? '' : editor.toDraft(field, cellValue(field, held))
}
